import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Button from "@amzn/awsui-components-react/polaris/button";
import Select, { SelectProps } from "@amzn/awsui-components-react/polaris/select";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import Flashbar, { FlashbarProps } from "@amzn/awsui-components-react/polaris/flashbar";
import Box from "@amzn/awsui-components-react/polaris/box";
import { API } from "aws-amplify";

// Disposition keys sourced from PharmacyClaimsService mock/dispositions_repo.go (commit b78225ac).
// If keys change upstream, update this list to match.
const DISPOSITION_KEYS: SelectProps.Option[] = [
  { label: "cant_fix:pa:pa_not_supported", value: "cant_fix:pa:pa_not_supported", description: "PA not supported" },
  { label: "cant_fix:pa:cancel_epa_cmm", value: "cant_fix:pa:cancel_epa_cmm", description: "Cancel EPA CMM" },
  { label: "cant_fix:pa:prescriber_denied", value: "cant_fix:pa:prescriber_denied", description: "Prescriber denied PA" },
  { label: "cant_fix:needs_manager_attention", value: "cant_fix:needs_manager_attention", description: "Needs manager attention" },
  { label: "cant_fix:needs_dev_attention", value: "cant_fix:needs_dev_attention", description: "Needs dev attention" },
  { label: "cant_fix:needs_pharmacist_attention", value: "cant_fix:needs_pharmacist_attention", description: "Needs pharmacist attention" },
  { label: "cant_fix:needs_reassignment:default_queue", value: "cant_fix:needs_reassignment:default_queue", description: "Reassign to default queue" },
  { label: "cant_fix:needs_reassignment:dur_queue", value: "cant_fix:needs_reassignment:dur_queue", description: "Reassign to DUR queue" },
  { label: "cant_fix:hard_failure:not_contracted", value: "cant_fix:hard_failure:not_contracted", description: "Not contracted" },
  { label: "cant_fix:hard_failure:pdmp_failure", value: "cant_fix:hard_failure:pdmp_failure", description: "PDMP failure" },
  { label: "cant_fix:hard_failure:reject_dur", value: "cant_fix:hard_failure:reject_dur", description: "DUR rejected" },
  { label: "cant_fix:hard_failure:rx_discontinued", value: "cant_fix:hard_failure:rx_discontinued", description: "Rx discontinued" },
  { label: "cant_fix:hard_failure:diagnosis_code", value: "cant_fix:hard_failure:diagnosis_code", description: "Diagnosis code issue" },
  { label: "cant_fix:hard_failure:fill_limit", value: "cant_fix:hard_failure:fill_limit", description: "Fill limit reached" },
  { label: "cant_fix:rts:override_denied", value: "cant_fix:rts:override_denied", description: "RTS override denied" },
  { label: "cant_fix:rebill_failed:original_reversal_rejected", value: "cant_fix:rebill_failed:original_reversal_rejected", description: "Rebill failed - reversal rejected" },
  { label: "cant_fix:rebill_failed:rebilled_payer_rejected", value: "cant_fix:rebill_failed:rebilled_payer_rejected", description: "Rebill failed - payer rejected" },
  { label: "cant_fix:customer_fix:incorrect_insurance", value: "cant_fix:customer_fix:incorrect_insurance", description: "Incorrect insurance" },
  { label: "cant_fix:customer_fix:incorrect_demographic", value: "cant_fix:customer_fix:incorrect_demographic", description: "Incorrect demographic" },
  { label: "pause:temp_failure", value: "pause:temp_failure", description: "Temporary failure" },
  { label: "pause:pa:pa_initiated", value: "pause:pa:pa_initiated", description: "PA initiated" },
  { label: "pause:pa:epa_send_pa_to_cmm", value: "pause:pa:epa_send_pa_to_cmm", description: "EPA send PA to CMM" },
  { label: "pause:pa:epa_more_info", value: "pause:pa:epa_more_info", description: "EPA more info needed" },
  { label: "pause:prescriber_pending", value: "pause:prescriber_pending", description: "Prescriber pending" },
  { label: "pause:rph_pending_prescriber_response", value: "pause:rph_pending_prescriber_response", description: "RPh pending prescriber response" },
  { label: "pause:rph_pending_customer_response", value: "pause:rph_pending_customer_response", description: "RPh pending customer response" },
  { label: "pause:cc:insurance_pending", value: "pause:cc:insurance_pending", description: "CC insurance pending" },
  { label: "pause:cc:pharmacy_unresponsive", value: "pause:cc:pharmacy_unresponsive", description: "CC pharmacy unresponsive" },
  { label: "pause:cc:prescriber_pending", value: "pause:cc:prescriber_pending", description: "CC prescriber pending" },
  { label: "pause:cc:pending_LVM_day1", value: "pause:cc:pending_LVM_day1", description: "CC pending LVM day 1" },
  { label: "pause:cc:pending_LVM_day2", value: "pause:cc:pending_LVM_day2", description: "CC pending LVM day 2" },
  { label: "pause:cc:pending_prescriber_day1", value: "pause:cc:pending_prescriber_day1", description: "CC pending prescriber day 1" },
  { label: "pause:cc:pharmacy_closed", value: "pause:cc:pharmacy_closed", description: "CC pharmacy closed" },
  { label: "actionable:edit_claim", value: "actionable:edit_claim", description: "Edit claim (send claim)" },
  { label: "actionable:pdmp_passed", value: "actionable:pdmp_passed", description: "PDMP passed" },
  { label: "actionable:cancel_manual_adjudication", value: "actionable:cancel_manual_adjudication", description: "Cancel manual adjudication" },
  { label: "actionable:mark_insurance_as_verified", value: "actionable:mark_insurance_as_verified", description: "Mark insurance as verified" },
  { label: "actionable:retry_claim", value: "actionable:retry_claim", description: "Resend/retry claim" },
  { label: "actionable:tech_cc:insurance_info_complete", value: "actionable:tech_cc:insurance_info_complete", description: "Insurance info complete" },
  { label: "actionable:clear_dur", value: "actionable:clear_dur", description: "Clear DUR" },
  { label: "actionable:rebilling_complete:insurance_updated", value: "actionable:rebilling_complete:insurance_updated", description: "Rebilling complete - insurance updated" },
  { label: "actionable:cancel_rebilling", value: "actionable:cancel_rebilling", description: "Cancel rebilling" },
  { label: "actionable:force_state_change:force_claim_rejected", value: "actionable:force_state_change:force_claim_rejected", description: "Force claim rejected" },
  { label: "actionable:force_state_change:force_claim_reversed", value: "actionable:force_state_change:force_claim_reversed", description: "Force claim reversed" },
  { label: "actionable:force_state_change:force_claim_paid", value: "actionable:force_state_change:force_claim_paid", description: "Force claim paid" },
  { label: "actionable:force_state_change:force_rebill_paid", value: "actionable:force_state_change:force_rebill_paid", description: "Force rebill paid" },
  { label: "actionable:force_state_change:force_rebill_reversed", value: "actionable:force_state_change:force_rebill_reversed", description: "Force rebill reversed" },
  { label: "claims_support:begin_rebilling", value: "claims_support:begin_rebilling", description: "Begin rebilling" },
  { label: "claims_support:begin_rebilling:pbm_request", value: "claims_support:begin_rebilling:pbm_request", description: "Begin rebilling - PBM request" },
  { label: "claims_support:begin_rebilling:internal_request", value: "claims_support:begin_rebilling:internal_request", description: "Begin rebilling - internal request" },
  { label: "claims_support:begin_transfer_to_billing_ops:automation_rejected", value: "claims_support:begin_transfer_to_billing_ops:automation_rejected", description: "Transfer to billing ops - automation rejected" },
  { label: "claims_support:begin_transfer_to_billing_ops:batch_reversal_failed", value: "claims_support:begin_transfer_to_billing_ops:batch_reversal_failed", description: "Transfer to billing ops - batch reversal failed" },
  { label: "claims_support:begin_transfer_to_billing_ops:dev_support", value: "claims_support:begin_transfer_to_billing_ops:dev_support", description: "Transfer to billing ops - dev support" },
  { label: "claims_support:determine_next_step:automation_rejected", value: "claims_support:determine_next_step:automation_rejected", description: "Determine next step - automation rejected" },
  { label: "claims_support:batch_reversal:claim_reversed", value: "claims_support:batch_reversal:claim_reversed", description: "Batch reversal success" },
  { label: "claims_support:begin_claim_editing", value: "claims_support:begin_claim_editing", description: "Begin claim editing" },
  { label: "claims_support:claim_editing_complete", value: "claims_support:claim_editing_complete", description: "Claim editing complete" },
  { label: "customer_support:begin_rebilling:customer_request", value: "customer_support:begin_rebilling:customer_request", description: "Customer rebilling request" },
  { label: "dev_support:begin_rebilling:internal_request", value: "dev_support:begin_rebilling:internal_request", description: "Dev support rebilling" },
  { label: "dev_support:edge_case:documentation", value: "dev_support:edge_case:documentation", description: "Dev support edge case documentation" },
  { label: "reassign:cc_insurance_pharmacy_queue", value: "reassign:cc_insurance_pharmacy_queue", description: "Reassign to insurance pharmacy queue" },
  { label: "reassign:needs_additional_information:needs_prescriber_outreach", value: "reassign:needs_additional_information:needs_prescriber_outreach", description: "Needs prescriber outreach" },
  { label: "reassign:pa_followup_queue", value: "reassign:pa_followup_queue", description: "Reassign to PA follow-up queue" },
  { label: "reassign:caapp-claim-editing-queue", value: "reassign:caapp-claim-editing-queue", description: "Reassign to claim editing queue" },
];

interface ApplyDispositionResponse {
  claimId: string;
  responseMessage: string;
}

const RESPONSE_PRE_STYLE: React.CSSProperties = {
  backgroundColor: "#f5f5f5",
  border: "1px solid lightgray",
  borderRadius: "4px",
  padding: "10px",
  overflowX: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "normal",
};

interface State {
  personId: string;
  claimId: string;
  dispositionKey: string;
  dispositionSetBy: string;
  dispositionFreeText: string;
  useCustomKey: boolean;
  isSubmitting: boolean;
  response: ApplyDispositionResponse | null;
  flashMessages: FlashbarProps.MessageDefinition[];
}

class ApplyClaimDispositionSection extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = {
      personId: "",
      claimId: "",
      dispositionKey: "",
      dispositionSetBy: "apaq-portal",
      dispositionFreeText: "",
      useCustomKey: false,
      isSubmitting: false,
      response: null,
      flashMessages: [],
    };
  }

  private handleSubmit = async () => {
    if (this.state.isSubmitting) return;

    const { personId, claimId, dispositionKey, dispositionSetBy, dispositionFreeText } = this.state;

    if (!personId.trim() || !claimId.trim() || !dispositionKey.trim() || !dispositionSetBy.trim()) {
      this.setState({
        flashMessages: [{ type: "error", content: "Person ID, Claim ID, Disposition Key, and Disposition Set By are all required.", dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
      });
      return;
    }

    this.setState({ isSubmitting: true, flashMessages: [], response: null });

    try {
      const body: any = {
        personId: personId.trim(),
        claimId: claimId.trim(),
        dispositionKey: dispositionKey.trim(),
        dispositionSetBy: dispositionSetBy.trim(),
      };
      if (dispositionFreeText.trim()) {
        body.dispositionFreeText = dispositionFreeText.trim();
      }

      const response = await API.post("ApaqPortalAPI", "/pharmacy-customer/claim/apply-disposition", { body });

      this.setState({
        isSubmitting: false,
        response,
        flashMessages: [{ type: "success", content: `Disposition applied successfully to claim ${response.claimId || claimId}.`, dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
      });
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to apply claim disposition";
      this.setState({
        isSubmitting: false,
        flashMessages: [{ type: "error", content: message, dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
      });
    }
  };

  render() {
    const { personId, claimId, dispositionKey, dispositionSetBy, dispositionFreeText, useCustomKey, isSubmitting, response, flashMessages } = this.state;
    const canSubmit = personId.trim() && claimId.trim() && dispositionKey.trim() && dispositionSetBy.trim();

    return (
      <SpaceBetween size="l">
        {flashMessages.length > 0 && <Flashbar items={flashMessages} />}

        <Container header={
          <Header
            variant="h3"
            actions={
              <Button
                variant="primary"
                onClick={this.handleSubmit}
                loading={isSubmitting}
                disabled={!canSubmit}
              >
                Apply Disposition
              </Button>
            }
          >
            Apply Claim Disposition
          </Header>
        }>
          <SpaceBetween size="l">
            <ColumnLayout columns={2}>
              <FormField label="Person ID" description="Required">
                <Input
                  value={personId}
                  onChange={({ detail }) => this.setState({ personId: detail.value })}
                  placeholder="amzn1.actor.person.oid...."
                />
              </FormField>
              <FormField label="Claim ID" description="Required">
                <Input
                  value={claimId}
                  onChange={({ detail }) => this.setState({ claimId: detail.value })}
                  placeholder="6a236178e0c8ce02820014c8"
                />
              </FormField>
            </ColumnLayout>

            <ColumnLayout columns={2}>
              <FormField label="Disposition Key" description="Required">
                <SpaceBetween size="xs">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Button
                      variant={useCustomKey ? "normal" : "primary"}
                      onClick={() => this.setState({ useCustomKey: false, dispositionKey: "" })}
                    >
                      Select from known keys
                    </Button>
                    <Button
                      variant={useCustomKey ? "primary" : "normal"}
                      onClick={() => this.setState({ useCustomKey: true, dispositionKey: "" })}
                    >
                      Use custom key
                    </Button>
                  </div>
                  {!useCustomKey ? (
                    <Select
                      selectedOption={DISPOSITION_KEYS.find(o => o.value === dispositionKey) || null}
                      onChange={({ detail }) => this.setState({ dispositionKey: detail.selectedOption.value || "" })}
                      options={DISPOSITION_KEYS}
                      placeholder="Select a disposition key..."
                      filteringType="auto"
                    />
                  ) : (
                    <Input
                      value={dispositionKey}
                      onChange={({ detail }) => this.setState({ dispositionKey: detail.value })}
                      placeholder="Type a custom key (e.g. cant_fix:pa:custom_reason)"
                    />
                  )}
                </SpaceBetween>
              </FormField>
              <FormField label="Disposition Set By" description="Required — identity of the caller">
                <Input
                  value={dispositionSetBy}
                  onChange={({ detail }) => this.setState({ dispositionSetBy: detail.value })}
                  placeholder="apaq-portal"
                />
              </FormField>
            </ColumnLayout>

            <FormField label="Disposition Free Text" description="Optional — justification notes">
              <Textarea
                value={dispositionFreeText}
                onChange={({ detail }) => this.setState({ dispositionFreeText: detail.value })}
                placeholder="PA not supported for this plan"
                rows={3}
              />
            </FormField>
          </SpaceBetween>
        </Container>

        {response && (
          <Container header={<Header variant="h3">Response</Header>}>
            <Box>
              <pre style={RESPONSE_PRE_STYLE}>
                {JSON.stringify(response, null, 2)}
              </pre>
            </Box>
          </Container>
        )}
      </SpaceBetween>
    );
  }
}

export default ApplyClaimDispositionSection;
